import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import LoadingSpinner from '../../../components/LoadingSpinner';

const AchievementShowcase = ({ achievements, userId }) => {
  const { userProfile } = useAuth();
  const [badges, setBadges] = useState([]);
  const [progressStats, setProgressStats] = useState({});
  const [loading, setLoading] = useState(true);

  // Determine target user ID: use prop if provided, otherwise current user
  const targetUserId = userId || userProfile?.id;
  
  // Check if viewing own profile
  const isOwnProfile = !userId || userId === userProfile?.id;

  useEffect(() => {
    const fetchBadgesAndProgress = async () => {
      if (!targetUserId) return;

      try {
        setLoading(true);

        // Fetch all badges from database
        const { data: badgesData, error: badgesError } = await supabase?.from('badges')?.select('*')?.order('name');

        if (badgesError) throw badgesError;

        // Fetch user's earned badges from user_badges table
        const { data: userBadgesData, error: userBadgesError } = await supabase
          ?.from('user_badges')
          ?.select('badge_id, badges(*)')
          ?.eq('user_id', targetUserId);

        if (userBadgesError) {
          console.error('User badges fetch error:', userBadgesError);
        }

        const earnedBadgeIds = (userBadgesData || [])?.map(ub => ub?.badge_id);

        // Only fetch progress stats if viewing own profile
        let progressData = {};
        if (isOwnProfile) {
          const { data: progress, error: progressError } = await supabase?.rpc('get_user_gamification_progress', { target_user_id: targetUserId });

          if (progressError) {
            console.error('Progress fetch error:', progressError);
          } else {
            progressData = progress || {};
          }
        }

        setProgressStats(progressData);

        // Map badges with progress and earned status
        const mappedBadges = (badgesData || [])?.map(badge => {
          const currentCount = progressData?.[badge?.name] || 0;
          const targetCount = badge?.target_count || 1;
          const hasEarned = earnedBadgeIds?.includes(badge?.id);
          const progressPercentage = Math.min((currentCount / targetCount) * 100, 100);

          return {
            id: badge?.id,
            name: badge?.name,
            icon: badge?.icon || '🏆',
            description: badge?.description,
            category: badge?.category,
            targetCount: targetCount,
            currentCount: currentCount,
            hasEarned: hasEarned,
            progressPercentage: progressPercentage
          };
        });

        // If viewing someone else's profile, show only earned badges
        const filteredBadges = isOwnProfile ? mappedBadges : mappedBadges?.filter(b => b?.hasEarned);

        setBadges(filteredBadges);
      } catch (err) {
        console.error('Error fetching badges:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchBadgesAndProgress();
  }, [targetUserId, isOwnProfile, achievements]);

  if (loading) {
    return (
      <div className="bg-card rounded-xl shadow-organic p-4 md:p-6 lg:p-8">
        <h2 className="text-xl md:text-2xl font-bold text-foreground mb-4 md:mb-6 flex items-center gap-2">
          <Icon name="Trophy" size={24} />
          Başarılar
        </h2>
        <div className="flex justify-center items-center py-12">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (badges?.length === 0) {
    return (
      <div className="bg-card rounded-xl shadow-organic p-4 md:p-6 lg:p-8">
        <h2 className="text-xl md:text-2xl font-bold text-foreground mb-4 md:mb-6 flex items-center gap-2">
          <Icon name="Trophy" size={24} />
          Başarılar
        </h2>
        <div className="text-center py-12">
          <p className="text-muted-foreground">
            {isOwnProfile ? 'Henüz rozet bulunamadı' : 'Bu kullanıcı henüz rozet kazanmamış'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl shadow-organic p-4 md:p-6 lg:p-8">
      <h2 className="text-xl md:text-2xl font-bold text-foreground mb-4 md:mb-6 flex items-center gap-2">
        <Icon name="Trophy" size={24} />
        Başarılar
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        {badges?.map((badge) => (
          <div
            key={badge?.id}
            className={`rounded-lg p-4 border-2 transition-smooth ${
              badge?.hasEarned
                ? 'bg-gradient-to-br from-primary/10 to-secondary/10 border-primary/30 hover:shadow-organic'
                : 'bg-muted/30 border-border opacity-60'
            }`}
          >
            <div className="flex items-start gap-3 mb-3">
              <div
                className={`w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center flex-shrink-0 text-2xl ${
                  badge?.hasEarned
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {badge?.icon}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm md:text-base font-semibold text-foreground mb-1 line-clamp-1">
                  {badge?.name}
                </h3>
                <p className="text-xs md:text-sm text-muted-foreground line-clamp-2">
                  {badge?.description}
                </p>
              </div>
            </div>

            {badge?.hasEarned ? (
              <div className="flex items-center justify-center text-sm md:text-base">
                <span className="text-green-600 font-bold flex items-center gap-1">
                  <Icon name="Check" size={16} />
                  Kazanıldı! 🏆
                </span>
              </div>
            ) : (
              // Only show progress bars on own profile
              isOwnProfile && (
                <div className="space-y-2">
                  <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                    <div
                      className="bg-blue-600 h-2.5 rounded-full transition-smooth"
                      style={{ width: `${badge?.progressPercentage}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 text-center">
                    {badge?.currentCount} / {badge?.targetCount} İlerleme
                  </p>
                </div>
              )
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AchievementShowcase;